/**
 * The catalog search's suggestions and history, as the app keeps them.
 *
 * While typing, the field suggests what people search for most (PopularSearchService over
 * `smart_search_popular_queries.csv`) and the categories whose names, Russian or English, or
 * aliases fit (CategorySearchService, a letter off allowed); with the field empty, the recent
 * searches, the most popular queries and the categories behind them.
 *
 * The data comes out of the app's resources (tools/build-catalog-search.ts), the captions the
 * cards are named with among it, and loads when the catalog first opens, in a chunk of its own.
 */
import { language } from '../i18n/I18n';
import { catalogTree, type CatalogFilter, type CatalogNode } from './catalog';

export interface SearchData {
  popularQueries: Array<[string, number]>;
  aliases: { ru: Array<[string, string]>; base: Array<[string, string]> };
  categoryAliases: Record<string, string[]>;
  /** A category's name in Russian, where the app's dictionary has it (I18n.Translate(category.Name)). */
  categoryNames: Record<string, string>;
  captions: Record<string, string>;
}

let data: SearchData | null = null;
let loading: Promise<SearchData> | null = null;

/** The app's search data; loaded once, on the first call. */
export function loadSearchData(): Promise<SearchData> {
  loading ??= import('./catalogSearch.json').then((module) => {
    const loaded = module as unknown as { default?: SearchData } & SearchData;
    data = loaded.default ?? loaded;
    return data;
  }, (error) => { loading = null; throw error; });
  return loading;
}

/** The data when it is already here. */
export function searchDataNow(): SearchData | null { return data; }

/**
 * A model's caption in the interface language: the search data holds the app's Russian for the English captions the
 * app's own models carry; in English a caption is its own name.
 */
export const captionIn = (captions: Record<string, string> | null | undefined, caption: string): string =>
  language() === 'ru' ? captions?.[caption] ?? caption : caption;

// ---------------------------------------------------------------------------------------------
// The app's word handling (ExtensionMethods).

/** ExtensionMethods.ForeignCharacters, as RemoveDiacritics walks it. */
// i18n-ignore-start: the app's table of look-alike letters for search normalization (a Cyrillic А and Т among them), not interface text
const FOREIGN: Array<[string, string]> = [
  ['äæ', 'a'], ['ö', 'o'], ['ü', 'u'], ['Ä', 'A'], ['Ü', 'U'], ['Ö', 'O'],
  ['ÀÁÂÃÄÅǺĀĂĄǍΑΆẢẠẦẪẨẬẰẮẴẲẶА', 'A'], ['àáâãåǻāăąǎªάảạầấẫẩậằắẵẳặ', 'a'], ['ÇĆĈĊČ', 'C'], ['çćĉċč', 'c'],
  ['ÐĎĐ', 'D'], ['ðďđ', 'd'], ['ÈÉÊËĒĔĖĘĚΕΈẼẺẸỀẾỄỂỆ', 'E'], ['èéêëēĕėęěẽẻẹềếễểệ', 'e'], ['ĜĞĠĢ', 'G'], ['ĝğġģ', 'g'],
  ['ĤĦ', 'H'], ['ĥħ', 'h'], ['ÌÍÎÏĨĪĬǏĮİΗΉΊΙΪỈỊ', 'I'], ['ìíîïĩīĭǐįıηήϊỉịї', 'i'], ['Ĵ', 'J'], ['ĵ', 'j'], ['Ķ', 'K'], ['ķ', 'k'],
  ['ĹĻĽĿŁ', 'L'], ['ĺļľŀł', 'l'], ['ÑŃŅŇΝ', 'N'], ['ñńņňŉ', 'n'], ['ÒÓÔÕŌŎǑŐƠØǾΟΌỎỌỒỐỖỔỘỜỚỠỞỢ', 'O'],
  ['òóôõōŏǒőơøǿºοόỏọồốỗổộờớỡởợ', 'o'], ['ŔŖŘ', 'R'], ['ŕŗř', 'r'], ['ŚŜŞȘŠ', 'S'], ['śŝşșš', 's'], ['ȚŢŤŦТ', 'T'], ['țţťŧ', 't'],
  ['ÙÚÛŨŪŬŮŰŲƯǓǕǗǙǛŨỦỤỪỨỮỬỰ', 'U'], ['ùúûũūŭůűųưǔǖǘǚǜυύϋủụừứữửự', 'u'], ['ÝŸŶΥΎΫỲỸỶỴ', 'Y'], ['ýÿŷỳỹỷỵ', 'y'],
  ['Ŵ', 'W'], ['ŵ', 'w'], ['ŹŻŽΖ', 'Z'], ['źżž', 'z'], ['ÆǼ', 'A'],
];
// i18n-ignore-end
const FOREIGN_MAP = new Map<string, string>();
for (const [chars, plain] of FOREIGN) for (const char of chars) if (!FOREIGN_MAP.has(char)) FOREIGN_MAP.set(char, plain);

export const stripDiacritics = (text: string): string => [...text].map((char) => FOREIGN_MAP.get(char) ?? char).join('');
/** CategorySearchService.Normalize. */
export const normalize = (text: string): string => stripDiacritics(text.toLowerCase()).trim();

// ---------------------------------------------------------------------------------------------
// Suggestions.

/** Levenshtein distance up to `limit` (BKTree.Search with maxDistance 1). */
function within(a: string, b: string, limit: number): boolean {
  if (Math.abs(a.length - b.length) > limit) return false;
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      best = Math.min(best, current[j]);
    }
    if (best > limit) return false;
    previous = current;
  }
  return previous[b.length] <= limit;
}

/** PopularSearchService.Search: what people typed that fits the input, most frequent first. */
export function popularQueries(input: string, max = 4, source: SearchData | null = data): string[] {
  if (!source) return [];
  const entries = source.popularQueries;
  const byFrequency = (list: Array<[string, number]>) => list.slice().sort((a, b) => b[1] - a[1]);
  if (!input.trim()) return byFrequency(entries).slice(0, max).map(([query]) => query);
  const endsWithSpace = input.endsWith(' ');
  const text = normalize(input);
  const results: string[] = [];
  const add = (query: string) => { if (!results.includes(query)) results.push(query); return results.length >= max; };
  const parts = text.split(' ').filter(Boolean);
  if (input.includes(' ') && (parts.length > 1 || endsWithSpace)) {
    if (endsWithSpace) {
      const start = parts.join(' ') + ' ';
      for (const [query] of byFrequency(entries.filter(([query]) => query.startsWith(start)))) if (add(query)) break;
      return results;
    }
    const prefix = parts.slice(0, -1).join(' '), last = parts[parts.length - 1];
    for (const [query] of byFrequency(entries.filter(([query]) => query.startsWith(`${prefix} ${last}`)))) if (add(query)) return results;
    for (const [query] of byFrequency(entries.filter(([query]) => query.startsWith(`${prefix} `) && query.includes(last) && !results.includes(query)))) if (add(query)) return results;
    for (const [fuzzy] of entries.filter(([query]) => within(last, query, 1))) {
      for (const [query] of byFrequency(entries.filter(([query]) => query.startsWith(`${prefix} ${fuzzy}`) && !results.includes(query)))) if (add(query)) return results;
    }
    return results;
  }
  for (const [query] of byFrequency(entries.filter(([query]) => query.startsWith(text)))) if (add(query)) return results;
  for (const [query] of byFrequency(entries.filter(([query]) => query.includes(text) && !results.includes(query)))) if (add(query)) return results;
  for (const word of text.split(' ').filter(Boolean).map(normalize)) {
    for (const [query] of entries) {
      if (results.includes(query)) continue;
      if ((query.includes(word) || word.includes(query)) && add(query)) return results;
    }
    for (const [query] of entries) if (within(word, query, 1) && add(query)) return results;
  }
  return results;
}

/** A category of the drawer's tree a suggestion opens: a list, a folder, or a list's chip. */
export interface CategoryTarget { node: CatalogNode; path: CatalogNode[]; filter?: CatalogFilter; title: string; name: string }

let everyTarget: CategoryTarget[] | null = null;
/** The tree in the drawer's order: every folder and list, a list's chips after it. */
function targets(): CategoryTarget[] {
  if (everyTarget) return everyTarget;
  const out: CategoryTarget[] = [];
  const walk = (nodes: CatalogNode[], path: CatalogNode[]) => {
    for (const node of nodes) {
      out.push({ node, path, title: node.title, name: node.name });
      if (node.children?.length) walk(node.children, [...path, node]);
      else for (const filter of node.filters ?? []) out.push({ node, path, filter, title: filter.title, name: filter.id });
    }
  };
  walk(catalogTree(), []);
  everyTarget = out;
  return out;
}

/** CategorySearchService's index: every keyword and the first category it names. */
let index: Array<[string, CategoryTarget]> | null = null;
function indexOf(source: SearchData): Array<[string, CategoryTarget]> {
  if (index) return index;
  const ruAliases = source.aliases.ru.map(([alias, target]) => [normalize(alias), normalize(target)] as const);
  const seen = new Set<string>();
  const out: Array<[string, CategoryTarget]> = [];
  for (const target of targets()) {
    // The English name, its Russian (the app's dictionary), the title the tree shows, the aliases.
    const name = normalize(target.name), translated = normalize(source.categoryNames[target.name] ?? target.name), title = normalize(target.title);
    const keywords = new Set<string>([name]);
    if (translated && translated !== name) keywords.add(translated);
    if (title && title !== name && title !== translated) keywords.add(title);
    for (const alias of source.categoryAliases[target.name] ?? []) keywords.add(normalize(alias));
    for (const [alias, aliasTarget] of ruAliases) if (aliasTarget === name) keywords.add(alias);
    for (const keyword of keywords) if (keyword && !seen.has(keyword)) { seen.add(keyword); out.push([keyword, target]); }
  }
  index = out;
  return out;
}

/** CategorySearchService.Search: the categories whose keywords fit the input. */
export function categorySuggestions(input: string, max = 10, source: SearchData | null = data): CategoryTarget[] {
  if (!source) return [];
  const keywords = indexOf(source);
  const text = normalize(input);
  if (!text) return [];
  const results: CategoryTarget[] = [];
  const add = (target: CategoryTarget) => { if (!results.includes(target)) results.push(target); return results.length >= max; };
  for (const [keyword, target] of keywords) if (keyword.startsWith(text) && add(target)) return results;
  for (const [keyword, target] of keywords) if (keyword.includes(text) && add(target)) return results;
  for (const word of text.split(' ').filter(Boolean).map(normalize)) {
    for (const [keyword, target] of keywords) {
      if ((keyword.includes(word) || word.includes(keyword) || keyword.startsWith(word) || word.startsWith(keyword)) && add(target)) return results;
    }
    for (const [keyword, target] of keywords) if (within(word, keyword, 1) && add(target)) return results;
  }
  return results;
}

/** SmartAutocompleteService.GetPopularCategories: the categories behind the most popular queries. */
export function popularCategories(max = 8, source: SearchData | null = data): CategoryTarget[] {
  if (!source) return [];
  const results: CategoryTarget[] = [];
  for (const query of popularQueries('', max, source)) {
    const found = categorySuggestions(query, 1, source);
    if (found.length === 1 && !results.some((target) => target.name === found[0].name)) results.push(found[0]);
  }
  return results;
}

/** SmartAutocompleteService.HighlightMatch: the part after the typed letters is the one set in bold. */
export function highlight(source: string, query: string): { before: string; match: string; after: string } | null {
  const at = query ? source.toLowerCase().indexOf(query.toLowerCase()) : -1;
  return at < 0 ? null : { before: source.slice(0, at), match: source.slice(at, at + query.length), after: source.slice(at + query.length) };
}

/** ExtensionMethods.ToCapitalization: a suggestion starts with a capital. */
export const capitalized = (text: string): string => text ? text[0].toUpperCase() + text.slice(1) : text;

// ---------------------------------------------------------------------------------------------
// History (CatalogSearchHistory): the last eight searches of this browser.

export const HISTORY_SIZE = 8;

/** A search added to the front of the history, dropped from where it was. */
export function withSearch(history: string[], search: string): string[] {
  if (!search.trim()) return history;
  return [search, ...history.filter((entry) => entry !== search)].slice(0, HISTORY_SIZE);
}

/** For tests: forget what was built from the data. */
export function resetSearchIndex(): void { index = null; everyTarget = null; }
